import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EditIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Party, PartyType } from '../../types/party.types';
import { partyService } from '../../services/party.service';
interface PartyTableProps {
  parties: Party[];
  loading: boolean;
  canEdit: boolean;
}
export function PartyTable({
  parties,
  loading,
  canEdit
}: PartyTableProps) {
  const navigate = useNavigate();
  const getStatusVariant = (status: string): 'success' | 'danger' => {
    return status === 'Active' ? 'success' : 'danger';
  };
  const getTypeColor = (type: PartyType): string => {
    const colors = {
      [PartyType.CUSTOMER]: 'bg-green-100 text-green-800',
      [PartyType.VENDOR]: 'bg-blue-100 text-blue-800',
      [PartyType.EMPLOYEE]: 'bg-purple-100 text-purple-800',
      [PartyType.OTHER]: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors[PartyType.OTHER];
  };
  if (loading) {
    return <div className="bg-white rounded-lg border p-12 text-center text-gray-500" data-id="element-189">
        Loading parties...
      </div>;
  }
  return <div className="bg-white rounded-lg border overflow-hidden" data-id="element-190">
      <div className="overflow-x-auto" data-id="element-191">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-192">
          <thead className="bg-gray-50" data-id="element-193">
            <tr data-id="element-194">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-195">
                Party Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-196">
                Party Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-197">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-198">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-199">
                Mobile
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-200">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-201">
                Address
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-202">
                Associated Ledger
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-203">
                Ledger Balance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-204">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-205">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-206">
            {parties.length === 0 ? <tr data-id="element-207">
                <td colSpan={11} className="px-6 py-12 text-center text-gray-500" data-id="element-208">
                  No parties found. Click "Add Party" to create one.
                </td>
              </tr> : parties.map(party => <tr key={party.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/parties/${party.id}`)} data-id="element-209">
                  <td className="px-6 py-4 text-sm font-medium text-blue-600" data-id="element-210">
                    {party.partyCode}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900" data-id="element-211">
                    {party.name}
                  </td>
                  <td className="px-6 py-4" data-id="element-212">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getTypeColor(party.partyType)}`} data-id="element-213">
                      {party.partyType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-214">
                    {party.categoryName || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-215">
                    {party.mobile}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-216">
                    {party.email || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-217">
                    {party.address?.detailAddress ? `${party.address.detailAddress.substring(0, 30)}...` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-218">
                    {party.associatedLedger ? `${party.associatedLedger.accountCode} - ${party.associatedLedger.accountName}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-medium" data-id="element-219">
                    <span className={party.associatedLedger && party.associatedLedger.balance < 0 ? 'text-red-600' : 'text-gray-900'} data-id="element-220">
                      {party.associatedLedger ? partyService.formatCurrency(party.associatedLedger.balance) : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-221">
                    <Badge variant={getStatusVariant(party.status)} data-id="element-222">
                      {party.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4" onClick={e => e.stopPropagation()} data-id="element-223">
                    <div className="flex items-center justify-center gap-2" data-id="element-224">
                      <button onClick={() => navigate(`/parties/${party.id}`)} className="text-gray-600 hover:text-gray-900" title="View" data-id="element-225">
                        <EyeIcon className="w-4 h-4" data-id="element-226" />
                      </button>
                      {canEdit && party.isEditable && party.partyType !== PartyType.EMPLOYEE && <button onClick={() => navigate(`/parties/${party.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-227">
                            <EditIcon className="w-4 h-4" data-id="element-228" />
                          </button>}
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}